import React from 'react';
import { WebsiteDesignProps } from './types';
import { Heart, Stethoscope, PawPrint } from 'lucide-react';
import { Button } from "@/components/ui/button";

const VeterinaryMedicine: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "College of Veterinary Medicine";

    return (
        <div className="bg-white min-h-screen font-sans text-slate-800">
            <nav className="border-b border-slate-100 px-8 py-5 flex justify-between items-center bg-white sticky top-0 z-50">
                <div className="flex items-center gap-3 text-xl font-bold text-[#2e7d32]">
                    <PawPrint fill="currentColor" fillOpacity={0.2} /> {schoolName}
                </div>
                <div className="hidden md:flex gap-8 text-sm font-medium text-slate-500">
                    <a href="#" className="hover:text-[#2e7d32] transition-colors">Programs</a>
                    <a href="#" className="hover:text-[#2e7d32] transition-colors">Clinics</a>
                    <a href="#" className="hover:text-[#2e7d32] transition-colors">Research</a>
                    <a href="#" className="hover:text-[#2e7d32] transition-colors">Alumni</a>
                </div>
                <Button className="bg-[#2e7d32] hover:bg-[#1b5e20] text-white font-medium rounded-full px-6">
                    Apply Now
                </Button>
            </nav>

            <header className="bg-[#e8f5e9] py-20 px-8">
                <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 bg-white border border-[#c8e6c9] text-[#2e7d32] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
                            <Heart size={14} /> Compassion in Action
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-[#1b5e20] mb-6 leading-tight">
                            Healing <br/> <span className="text-[#66bb6a]">All Creatures.</span>
                        </h1>
                        <p className="text-lg text-slate-600 mb-10 max-w-md leading-relaxed">
                            Develop the clinical skills and compassionate mindset to care for animal health and welfare around the world.
                        </p>
                        <div className="flex gap-4">
                            <Button className="bg-[#2e7d32] hover:bg-[#1b5e20] text-white font-bold px-8 h-12 rounded-lg">
                                DVM Program
                            </Button>
                            <Button variant="outline" className="border-[#2e7d32] text-[#2e7d32] hover:bg-[#2e7d32] hover:text-white font-bold px-8 h-12 rounded-lg">
                                Graduate Studies
                            </Button>
                        </div>
                    </div>

                    <div className="relative">
                        <div className="aspect-square rounded-full overflow-hidden border-8 border-white shadow-2xl max-w-md mx-auto">
                            <img 
                                src="https://images.unsplash.com/photo-1628009368231-7bb7cf61bfe9?q=80&w=2070&auto=format&fit=crop" 
                                className="w-full h-full object-cover" 
                                alt="Veterinarian" 
                            />
                        </div>
                        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-lg px-6 py-4 flex items-center gap-4">
                            <Stethoscope className="text-[#2e7d32]" />
                            <div>
                                <div className="font-bold text-slate-900">98%</div>
                                <div className="text-xs text-slate-500">Board Pass Rate</div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <section className="py-20 container mx-auto px-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { title: "Small Animal", desc: "Companion animal medicine and surgery." },
                        { title: "Equine", desc: "Horse health, sport medicine, and lameness." },
                        { title: "Wildlife", desc: "Conservation and exotic species care." },
                    ].map((item, i) => (
                        <div key={i} className="p-8 bg-slate-50 rounded-xl border border-slate-100 hover:shadow-lg transition-shadow">
                            <PawPrint className="text-[#66bb6a] mb-4" />
                            <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                            <p className="text-slate-500 leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default VeterinaryMedicine;
